import { Controller } from "@hotwired/stimulus";

const EMBEDPDF_URL =
  "https://cdn.jsdelivr.net/npm/@embedpdf/snippet@2.14.4/dist/embedpdf.js";
const EMBEDPDF_WASM_URL =
  "https://cdn.jsdelivr.net/npm/@embedpdf/snippet@2.14.4/dist/pdfium.wasm";

// Read-only viewer: hide every editing, export, and navigation panel.
const DISABLED_CATEGORIES = [
  "annotation",
  "redaction",
  "signature",
  "stamp",
  "form",
  "document-print",
  "export",
  "search",
  "panel",
  "panel-sidebar",
  "thumbnail",
  "fullscreen",
  "capture",
  "history",
  "bookmark",
  "attachment",
];

// Renders a PDF with EmbedPDF into the container target.
//
// Inline: the viewer loads on connect.
// In a modal: add a dialog target and trigger `pdf-viewer#open`; the viewer
// loads the first time the dialog opens.
//
// Connects to data-controller="pdf-viewer"
export default class extends Controller {
  static targets = ["container", "dialog"];

  static values = {
    url: String,
    // "light", "dark", or "site" to follow the site's current theme
    theme: { type: String, default: "light" },
  };

  connect() {
    this.unsubscribers = [];
    this.viewerElement = null;

    if (!this.hasDialogTarget) this.load();
  }

  disconnect() {
    this.teardown();
  }

  open(event) {
    if (!this.hasDialogTarget) return;
    // Let cmd/ctrl/shift-click open the PDF the browser's way
    if (event?.metaKey || event?.ctrlKey || event?.shiftKey) return;

    event?.preventDefault();
    this.dialogTarget.showModal();
    this.load();
  }

  async load() {
    if (this.viewerElement || this.loading) return;
    if (!this.hasContainerTarget || !this.urlValue) return;

    this.loading = true;

    try {
      const { default: EmbedPDF, ZoomMode } = await import(EMBEDPDF_URL);

      this.viewerElement = EmbedPDF.init({
        type: "container",
        target: this.containerTarget,
        src: this.urlValue,
        wasmUrl: EMBEDPDF_WASM_URL,
        tabBar: "never",
        disabledCategories: DISABLED_CATEGORIES,
        scroll: {
          defaultStrategy: "vertical",
          defaultPageGap: 12,
        },
        zoom: {
          defaultZoomLevel: ZoomMode.FitWidth,
        },
        theme: { preference: this.themePreference },
        permissions: { enforceDocumentPermissions: false },
      });

      if (!this.viewerElement) return;

      const registry = await this.viewerElement.registry;
      const scroll = registry.getPlugin("scroll")?.provides();
      const zoom = registry.getPlugin("zoom")?.provides();

      if (!scroll || !zoom) return;

      this.unsubscribers.push(
        scroll.onLayoutReady((event) => {
          if (!event.isInitial) return;

          zoom
            .forDocument(event.documentId)
            ?.requestZoom?.(ZoomMode.FitWidth);
        }),
      );
    } catch (error) {
      console.error("Failed to initialize PDF viewer", error);
    } finally {
      this.loading = false;
    }
  }

  get themePreference() {
    if (this.themeValue !== "site") return this.themeValue;

    return document.documentElement.dataset.theme === "dark" ? "dark" : "light";
  }

  teardown() {
    this.unsubscribers?.forEach((unsubscribe) => unsubscribe?.());
    this.unsubscribers = [];

    if (this.hasContainerTarget) {
      this.containerTarget.replaceChildren();
    }

    this.viewerElement = null;
  }
}
